import { APIToken } from '@/client';
import { DateFormats } from '@/system-settings/enums';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import moment from 'moment';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledTokenCard = styled.div<{ isNew?: boolean }>`
  background: var(--white-main);
  border: 1px solid
    ${({ isNew }) => (isNew ? 'var(--green-main)' : 'var(--gray-light)')};
  border-radius: var(--medium-border-radius);
  padding: var(--small-padding) var(--default-padding);
  margin-top: var(--default-padding);

  p {
    margin: 0;
  }

  .token-name-text {
    display: flex;
    align-items: center;
    height: 50px;
    padding: 0 var(--default-padding);
    padding-right: 0;
  }
`;

const StyledTokenValue = styled(Password)`
  &.p-password {
    width: 100%;
  }
`;

const StyledTimestamp = styled.span`
  color: var(--gray-darker);
  font-size: var(--xxsmall-font-size);
`;

const StyledNewBadge = styled.span`
  background: var(--green-main);
  color: var(--white-main);
  font-size: var(--xxsmall-font-size);
  font-weight: 600;
  padding: 2px 8px;
  border-radius: var(--xsmall-border-radius);
  text-transform: uppercase;
`;

const StyledTokenName = styled.div`
  display: flex;
  align-items: center;
  min-width: 30%;
  gap: 8px;
`;

// Tokens created before the creator was recorded, or whose creator was
// deleted, have no creator; the line is then left out instead of guessing.
const getCreatedByLabel = (
  createdBy: APIToken['createdBy'],
): string | undefined => {
  if (!createdBy) {
    return undefined;
  }
  const fullName = [createdBy.firstName, createdBy.lastName]
    .filter(Boolean)
    .join(' ');

  return fullName || createdBy.username || undefined;
};

interface APITokenCardCardProps {
  token: APIToken;
  onCopy: (text: string) => void;
  onDelete: (tokenId: string) => void;
  onUpdate?: (tokenId: string, newName: string) => void;
}

export const APITokenCard: React.FC<APITokenCardCardProps> = ({
  token,
  onCopy,
  onDelete,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const [editingTokenName, setEditingTokenName] = useState(token.name);
  const createdByLabel = getCreatedByLabel(token.createdBy);

  return (
    <StyledTokenCard isNew={token.isNew}>
      <FlexContainer direction="column" gap={12}>
        <FlexContainer justify="space-between" align="center">
          <FlexContainer
            align="center"
            justify="flex-start"
            gap={12}
            className="mt-3"
          >
            {!token.isNew ? (
              <StyledTokenName>
                <InputText
                  value={editingTokenName || t('api.tokens.fallback.name')}
                  className="w-full"
                  onChange={(e) => setEditingTokenName(e.target.value)}
                  onBlur={() => {
                    if (editingTokenName !== token.name) {
                      onUpdate?.(token.id, editingTokenName);
                    }
                  }}
                />
              </StyledTokenName>
            ) : (
              <span className="token-name-text">{editingTokenName}</span>
            )}

            {token.isNew ? (
              <StyledNewBadge>{t('generic.new')}</StyledNewBadge>
            ) : (
              <StyledTimestamp>
                {moment(token.created).format(DateFormats.TIMEDATE_SHORT)}
              </StyledTimestamp>
            )}
          </FlexContainer>
          <AppButton
            icon="pi pi-trash"
            type="text"
            severity="danger"
            size="xs"
            onClick={() => onDelete(token.id)}
            ariaLabel={t('api.tokens.delete')}
            isDisabled={token.isNew}
          />
        </FlexContainer>

        {!token.isNew && createdByLabel && (
          <StyledTimestamp data-testid="token-created-by">
            {t('api.tokens.createdBy')}: {createdByLabel}
          </StyledTimestamp>
        )}

        <FlexContainer>
          <StyledTokenValue
            value={token.isNew ? token.token : '*'.repeat(24)}
            disabled={!token.isNew}
            toggleMask={token.isNew}
          />

          {token.isNew && (
            <AppButton
              icon="pi pi-copy"
              type="text"
              severity="secondary"
              size="xs"
              onClick={() => onCopy(token.token)}
              ariaLabel={t('api.tokens.copy')}
            />
          )}
        </FlexContainer>
      </FlexContainer>
    </StyledTokenCard>
  );
};
